<script lang="ts">
    import { onMount } from 'svelte';
    import { App, View, f7ready } from 'framework7-svelte';
    import { _ } from 'svelte-i18n';
    
    import MainPage from '../pages/MainPage.svelte';
    import ProjectsPage from '../pages/ProjectsPage.svelte';
    import ProjectPage from '../pages/ProjectPage.svelte';
    import Callback from '../pages/Callback.svelte';
    import Account from '../pages/Account.svelte';
    import type { Router } from 'framework7/types';
    import { setErrorToastF7 } from '../api/errors';
    import { setNotifyToastF7 } from '../utils/notify';
    import { darkmode } from '../utils/darkmode';
    import { getUserManager, authState } from '../auth/auth';
    import { user } from '../auth/user';
    import { fetchPermissions } from '../utils/permissions';
    import ImportsLoginScreen from "./ImportsLoginScreen.svelte";
    import ImportsPage from "../pages/ImportsPage.svelte";

    let routes: Router.RouteParameters[] = [
        { name: 'main', path: '/', component: MainPage },
        { name: 'projects', path: '/projects', component: ProjectsPage },
        { name: 'project', path: '/project/:projectId', component: ProjectPage },
        { name: 'project-dashboard', path: '/project/:projectId/dashboard', component: ProjectPage },
        { name: 'project-insights', path: '/project/:projectId/insights', component: ProjectPage },
        { name: 'callback', path: '/callback/', component: Callback, keepAlive: false },
        { name: 'account', path: '/account', component: Account },
        { name: 'imports', path: '/import', component: ImportsPage },
    ];

    let f7router: Router.Router;
    let unsubscribe;

    onMount(() => {
        initAuthGuard(window.location.pathname);

        let lastToken: string | null = null;

        // get new permissions when token changed
        unsubscribe = user.subscribe(async (u) => {
            if (u && !u.expired && u.access_token !== lastToken) {
                lastToken = u.access_token;

                await fetchPermissions(u.profile.sub);
            }
        });

        return () => unsubscribe();
    });

    async function initAuthGuard(path: string) {
        authState.set('checking');
        
        const userManager = getUserManager();
        const u = await userManager.getUser();
        user.set(u);

        // Set public routes here
        const isPublic = path === '/' || path.startsWith('/callback');

        if (isPublic) {
            authState.set('ready');
            return;
        }

        if (!u || u.expired) {
            authState.set('redirecting');

            userManager.signinRedirect({
                state: { redirectUrl: path }
            });

            return;
        }

        authState.set('ready');
    }

    onMount(() => {
        const userManager = getUserManager();
        const onLoaded = (newUser) => {
            user.set(newUser);
        };

        const onUnloaded = () => {
            user.set(null);
        };

        userManager.events.addUserLoaded(onLoaded);
        userManager.events.addUserUnloaded(onUnloaded);

        return () => {
            userManager.events.removeUserLoaded(onLoaded);
            userManager.events.removeUserUnloaded(onUnloaded);
        };
    });

    onMount(async () => {
        f7ready((f7) => {
            f7router = f7.views.main.router;
            setErrorToastF7(f7);
            setNotifyToastF7(f7);
            f7.setColorTheme('#08A967');
            f7router.on('routeChanged', (newRoute) => {
                history.pushState({}, '', newRoute.path);
            });

            window.addEventListener('popstate', () => {
                f7router.back();
            });
        });
    });
</script>

{#if $authState === 'ready'}
    <!-- Main Framework7 App component where we pass Framework7 params -->
    <App theme="md" name="My App" id="com.demoapp.test" {routes} darkMode={$darkmode}>
        <ImportsLoginScreen/>
        <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined" rel="stylesheet" />

        <!-- Main view -->
        <View main masterDetailBreakpoint={700}></View>
    </App>
{:else}
    <div>{$_('shared.LOADING')}</div>
{/if}

<style>
    :global(body) {
        --f7-page-master-detail-width: 33%;
    }

    :global(.button .icon) {
        margin-right: 0.2em;
    }
</style>
